import React, { useState } from "react";
import { useMutation } from "@apollo/client";
import CloseIcon from "@material-ui/icons/Close";
import { IconButton } from "@material-ui/core";
import { DPFileUpload } from "Components/DP";
import { errorToast, successToastComponent } from "Modules/DPToast";
import { validateFile } from "./services/validateFile";
import { ConfirmationDialog } from "./ConfirmarionDialog/ConfirmarionDialog";
import { processFile } from "./services/processFile";
import { usePartnerConfig } from "Hooks/usePartnerConfig";
import mutations from "Shared/mutations";
import { DisbursementReasonCategory, UploadBatchDisbursementMutation, UploadBatchDisbursementMutationVariables } from "generated/graphql";
import { handleValidationErrors } from "Modules/HandleValidationErrors";
import { SuccessToastMessage } from "./SuccessToastMessage/SuccessToastMessage";
import { useStyles } from "./styles";

const REQUIRED_HEADERS = [
  "user_id",
  "amount",
  "account_number",
  "routing_number"
];

const DISBURSEMENTS_MAX_COUNT = 100;

export interface DialogParams {
  file?: File;
  totalCount: number;
  totalAmount: number;
}

export function FileUploadButton() {
  const [uploadDisbursementsFile] = useMutation<
    UploadBatchDisbursementMutation,
    UploadBatchDisbursementMutationVariables
  >(mutations.UPLOAD_BATCH_DISBURSEMENT_FILE);
  const classes = useStyles();

  const initialDialogParams = {
    file: undefined,
    totalCount: 0,
    totalAmount: 0,
  };

  const [dialogParams, setDialogParams] = useState<DialogParams>(initialDialogParams);

  const handleClose = () => {
    setDialogParams(initialDialogParams);
  };

  const handleConfirmFileUpload = async (reason: DisbursementReasonCategory) => {
    try {
      const response = await uploadDisbursementsFile({
        variables: {
          input: {
            attachment: dialogParams.file,
          },
          reason,
        },
      });

      const responseMessage = response?.data?.uploadBatchDisbursement?.message;
      if (responseMessage) {
        successToastComponent(
          <SuccessToastMessage />,
          {
            style: { width: 800 },
            bodyClassName: classes.toastContainer,
            closeButton: <IconButton><CloseIcon /></IconButton>,
          }
        );
      }
    } catch (err) {
      handleValidationErrors(err);
    }
    handleClose();
  };

  const { hasMultiplePaygroups } = usePartnerConfig();
  const requriedHeaders = hasMultiplePaygroups
    ? [...REQUIRED_HEADERS, "paygroup"]
    : REQUIRED_HEADERS;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleFileUpload = (e: any) => {
    const file = e.target.files[0] as File;
    const reader = new FileReader();
    reader.onload = (evt) => {
      const { missingHeaders, isEmpty } = validateFile(evt, requriedHeaders);
      if (missingHeaders?.length) {
        const message = `The uploaded file is missing the following headers: ${missingHeaders.join(", ")}.`;
        errorToast(message);
        return;
      } else if (isEmpty) {
        const message = "The uploaded file is empty";
        errorToast(message);
        return;
      }

      const transferData = processFile(evt);

      if (transferData.totalCount > DISBURSEMENTS_MAX_COUNT) {
        const message = `The batch file was not processed because it exceeds the limit of ${DISBURSEMENTS_MAX_COUNT} payments per file. Please resubmit a new file with ${DISBURSEMENTS_MAX_COUNT} or less payments.`;

        errorToast(message);
        return;
      }

      setDialogParams({
        ...transferData,
        file,
      });
    };
    reader.readAsArrayBuffer(file);
  };

  return (
    <>
      <DPFileUpload accept=".csv,.xlsx,.xls" onChange={handleFileUpload}>
        Upload
      </DPFileUpload>
      <ConfirmationDialog
        {...dialogParams}
        onConfirm={handleConfirmFileUpload}
        onClose={handleClose}
      />
    </>
  );
}
